import React from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, X } from 'lucide-react';
import { TableHead } from './table';

/**
 * Click-to-sort column header. Drop-in replacement for `<TableHead>Label</TableHead>`.
 *
 * Usage:
 *   const [sortKey, sortDir, toggle] = useSort('issue_date', 'desc');
 *   ...
 *   <SortableHeader column="customer" sortKey={sortKey} sortDir={sortDir} onSort={toggle}>
 *     Customer
 *   </SortableHeader>
 */
export const SortableHeader = ({
  column,
  sortKey,
  sortDir,
  onSort,
  align = 'left',
  children,
  testIdPrefix = 'sort',
  className = '',
}) => {
  const isActive = sortKey === column;
  const Icon = !isActive ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  const alignClass = align === 'right' ? 'ml-auto' : '';
  const headClass = align === 'right' ? 'text-right' : '';
  return (
    <TableHead className={`${headClass} ${className}`.trim()}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`flex items-center font-medium hover:text-slate-900 ${alignClass}`.trim()}
        data-testid={`${testIdPrefix}-${column}`}
      >
        {children}
        <Icon className={`inline h-3 w-3 ml-1 ${isActive ? '' : 'opacity-40'}`} />
      </button>
    </TableHead>
  );
};

/**
 * Tiny "Reset view" pill — shows only when `show` is true. Clicking calls
 * `onReset`. Tables can pass any flag (e.g. `sortKey !== default || dirChanged`)
 * and a callback that calls `resetSort()` plus any filter setters.
 */
export const ResetViewButton = ({ show, onReset, className = '', testId = 'reset-view-btn' }) => {
  if (!show) return null;
  return (
    <button
      type="button"
      onClick={onReset}
      className={`inline-flex items-center text-xs text-slate-500 hover:text-slate-900 underline-offset-4 hover:underline ${className}`.trim()}
      data-testid={testId}
    >
      <X className="h-3 w-3 mr-1" /> Reset view
    </button>
  );
};

/**
 * Reusable sort-state hook.
 * Returns [sortKey, sortDir, toggle(key), comparator(getValue)].
 *
 * `comparator(getValue)` returns a (a, b) => number that callers pass to
 * [...rows].sort(...). `getValue(row, key)` returns the primitive to compare.
 *
 * If `storageKey` is supplied, the latest [sortKey, sortDir] are persisted
 * to localStorage under that key — when the user comes back to the table
 * it opens with their preferred view restored.
 */
export const useSort = (initialKey, initialDir = 'asc', storageKey = null) => {
  const readStored = () => {
    if (!storageKey) return null;
    try {
      const raw = window.localStorage.getItem(`sort:${storageKey}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.key === 'string' && (parsed.dir === 'asc' || parsed.dir === 'desc')) {
        return parsed;
      }
    } catch (_) { /* corrupt entry — ignore and fall back to initial */ }
    return null;
  };

  const stored = readStored();
  const [sortKey, setSortKey] = React.useState(stored?.key ?? initialKey);
  const [sortDir, setSortDir] = React.useState(stored?.dir ?? initialDir);

  // Persist whenever sortKey/sortDir change (skip first render is unnecessary —
  // re-writing the same value is harmless and keeps logic simple).
  React.useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(
        `sort:${storageKey}`,
        JSON.stringify({ key: sortKey, dir: sortDir })
      );
    } catch (_) { /* quota exceeded / private mode — silently ignore */ }
  }, [storageKey, sortKey, sortDir]);

  const toggle = React.useCallback((key, defaultDirForKey) => {
    setSortKey(prevKey => {
      if (prevKey === key) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      setSortDir(defaultDirForKey || 'asc');
      return key;
    });
  }, []);
  const reset = React.useCallback(() => {
    setSortKey(initialKey);
    setSortDir(initialDir);
  }, [initialKey, initialDir]);
  const comparator = React.useCallback(
    (getValue) => (a, b) => {
      const va = getValue(a, sortKey);
      const vb = getValue(b, sortKey);
      // Nullish values sink to the bottom regardless of direction
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    },
    [sortKey, sortDir]
  );
  return [sortKey, sortDir, toggle, comparator, reset];
};
